import DateTextBox from "react-lib/apps/common/DateTextBox";

import { PAY_KINDS, payLineErrorOf, type PayLine, type PayMaster } from "./billingPayModelV4";

// Types
type BillingPayLineV4Props = {
  // data
  line: PayLine;
  tried: boolean;
  accounts: PayMaster[];
  edcs: PayMaster[];
  // callback
  onChange: (data: Partial<PayLine>) => void;
  onRemove: () => void;
  onCheckCoupon?: () => void;
};

/** รายละเอียดการชำระ 1 รายการ (บัตร / QR / เช็ค / คูปองเงินสด) ของ pop-up รับชำระและฝากเงิน */
const BillingPayLineV4 = (props: BillingPayLineV4Props) => {
  const { line } = props;

  const error = props.tried ? payLineErrorOf(line) : {};
  const isCard = line.kind === "cc" || line.kind === "dc";
  const bad = (on?: boolean) => (on ? " hv4-bill-pay-in--bad" : "");

  // Handler
  const handleChangeCard = (part: number, value: string) => {
    const card = [...line.card];

    card[part] = value.replace(/\D/g, "").slice(0, 4);

    props.onChange({ card });
  };

  // Render
  return (
    <div className="hv4-bill-pay-line">
      <button
        type="button"
        className="hv4-bill-pay-line-x"
        aria-label="ลบรายการชำระนี้"
        title="ลบ"
        onClick={props.onRemove}
      >
        ✕
      </button>
      <span className={`hv4-bill-pay-tag hv4-bill-pay-tag--${line.kind}`}>
        {PAY_KINDS.find((item) => item.kind === line.kind)?.label}
      </span>
      {isCard && (
        <>
          <div className="hv4-bill-pay-field">
            <label>เลขบัตร</label>
            {line.card.map((part, j) => (
              <input
                key={j}
                aria-label={`เลขบัตรชุดที่ ${j + 1}`}
                inputMode="numeric"
                maxLength={4}
                placeholder="xxxx"
                className={`hv4-bill-pay-in hv4-bill-pay-in--card${bad(j === 3 && error.card)}`}
                value={part}
                onChange={(e) => handleChangeCard(j, e.target.value)}
              />
            ))}
          </div>
          <div className="hv4-bill-pay-field">
            <label>
              เครื่องรูด <b className="hv4-bill-pay-req">*</b>
            </label>
            <select
              aria-label="เครื่องรูด"
              className={`hv4-bill-pay-in hv4-bill-pay-w200${bad(error.edc)}`}
              value={line.edc}
              onChange={(e) => props.onChange({ edc: e.target.value })}
            >
              <option value="">— เลือก —</option>
              {props.edcs.map((edc) => (
                <option key={edc.id} value={edc.id}>
                  {edc.name}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      {line.kind === "qr" && (
        <>
          <div className="hv4-bill-pay-field">
            <label>
              เลขที่อ้างอิง <b className="hv4-bill-pay-req">*</b>
            </label>
            <input
              aria-label="เลขที่อ้างอิง"
              placeholder="เลขอ้างอิงจากสลิป"
              className={`hv4-bill-pay-in hv4-bill-pay-w190${bad(error.ref)}`}
              value={line.ref}
              onChange={(e) => props.onChange({ ref: e.target.value })}
            />
          </div>
          <div className="hv4-bill-pay-field">
            <label>
              บัญชีรับ <b className="hv4-bill-pay-req">*</b>
            </label>
            <select
              aria-label="บัญชีรับ"
              className={`hv4-bill-pay-in hv4-bill-pay-w250${bad(error.account)}`}
              value={line.account}
              onChange={(e) => props.onChange({ account: e.target.value })}
            >
              <option value="">— เลือก —</option>
              {props.accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                  {account.account_no ? ` ${account.account_no}` : ""}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      {line.kind === "chq" && (
        <>
          <div className="hv4-bill-pay-field">
            <label>
              เลขเช็ค <b className="hv4-bill-pay-req">*</b>
            </label>
            <input
              aria-label="เลขเช็ค"
              className={`hv4-bill-pay-in hv4-bill-pay-w130${bad(error.cheque)}`}
              value={line.cheque}
              onChange={(e) => props.onChange({ cheque: e.target.value })}
            />
          </div>
          <div className="hv4-bill-pay-field">
            <label>
              วันที่เช็ค <b className="hv4-bill-pay-req">*</b>
            </label>
            <div className={`hv4-date hv4-bill-pay-date${bad(error.date)}`}>
              <DateTextBox
                placeholder="DD/MM/YYYY"
                value={line.date}
                onChange={(date: string) => props.onChange({ date })}
              />
            </div>
          </div>
        </>
      )}
      {line.kind === "cpn" && (
        <div className="hv4-bill-pay-field">
          <label>
            เลขคูปอง <b className="hv4-bill-pay-req">*</b>
          </label>
          <input
            aria-label="เลขคูปองเงินสด"
            placeholder="เช่น CV-000123"
            className={`hv4-bill-pay-in hv4-bill-pay-w170${bad(error.coupon || line.couponCheck === "bad")}`}
            value={line.coupon}
            onChange={(e) => props.onChange({ coupon: e.target.value, couponCheck: "" })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                props.onCheckCoupon?.();
              }
            }}
            onBlur={props.onCheckCoupon}
          />
        </div>
      )}
      <div className="hv4-bill-pay-field hv4-bill-pay-field--end">
        <label>
          จำนวน <b className="hv4-bill-pay-req">*</b>
        </label>
        <input
          aria-label="จำนวนเงิน"
          inputMode="decimal"
          className={`hv4-bill-pay-in hv4-bill-pay-w130 hv4-bill-num${bad(error.amount)}`}
          value={line.amount}
          onChange={(e) => props.onChange({ amount: e.target.value })}
        />
      </div>
    </div>
  );
};

BillingPayLineV4.displayName = "BillingPayLineV4";

export default BillingPayLineV4;
